<template>
  <template
    v-if="(rows[0] && rows[0].data._breadcrumbHeader) || checkHeader ? rowsHasThisHeader : true"
  >
    <thead>
      <BaseTableHeader
        :group-heading="groupHeading"
        v-model:is-collapsed="isCollapsed"
        :rows-length="rowsLength"
        :column-length="columnLength"
        ><template
          v-for="(slot, slotIndex) of Object.keys($slots)"
          :key="slotIndex"
          #[slot]="props"
        >
          <slot :name="slot" v-bind="props"></slot> </template
      ></BaseTableHeader>
      <BaseTableColumnHeaders
        :group-heading="groupHeading"
        :rows-length="rowsLength"
        :column-length="columnLength"
        :sorted-column="sortedColumn"
        :sorted-value="sortedValue"
        :columns="columns"
        :checkbox="checkbox"
        v-model:is-collapsed="isCollapsed"
        :all-checked="allChecked"
        @set-sort="setSort"
        @toggle-sort="toggleSort"
        @toggle-all-checkboxes="toggleAllCheckboxes"
      >
        <template v-for="(slot, slotIndex) of Object.keys($slots)" :key="slotIndex" #[slot]="props">
          <slot :name="slot" v-bind="props"></slot>
        </template>
      </BaseTableColumnHeaders>
    </thead>
  </template>
  <tbody v-if="!isCollapsed">
    <template v-for="row in rows" :key="JSON.stringify(row.data)">
      <BaseTableRow
        :index="useListIndex ? row.index : row.data._index"
        :row="row.data"
        :columns="columns"
        :filter="filter"
        :route="route"
        :checkbox="checkbox"
        v-model:checked-value="checkedItems[useListIndex ? row.index : row.data._index]"
        :cell-options="cellOptions"
        :show-checkbox="showCheckbox(row)"
      >
        <template v-for="(slot, slotIndex) of Object.keys($slots)" :key="slotIndex" #[slot]="props">
          <slot :name="slot" v-bind="props"></slot>
        </template>
      </BaseTableRow>
    </template>
  </tbody>
</template>
<script lang="ts">
import BaseTableHeader from './base_table/BaseTableHeader.vue'
import { computed, defineComponent, onBeforeMount, PropType, ref, Ref, watch } from 'vue'
import { size } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import { LanguageChoice } from '../i18n'
import {
  ColumnsInterface,
  FilteringDisplayOptions,
  DataRow,
} from '../types/components/data-rendering'
import BaseTableColumnHeaders from './base_table/BaseTableColumnHeaders.vue'
import BaseTableRow from './base_table/BaseTableRow.vue'
import { useCheckboxes } from './base_table/useCheckboxes'

export default defineComponent({
  components: {
    BaseTableHeader,
    // TableCellRender,
    BaseTableColumnHeaders,
    BaseTableRow,
  },
  emits: ['toggleCollapse', 'toggleSort', 'setSort', 'checkedChange', 'collapsed'],
  props: {
    // The column header data objects {name, label, type}
    columns: {
      required: true,
      type: Object as PropType<ColumnsInterface>,
    },
    //Column that is being sorted
    sortedColumn: {
      required: false,
      type: String,
      default: null,
    },
    //Sorted asc or desc
    sortedValue: {
      required: false,
      type: Boolean,
      default: null,
    },
    // Expand or hide the table data, excluding the heading
    collapsed: {
      default: true,
      type: Boolean,
    },
    // The search filter text that passes down to the table cells
    filter: {
      required: false,
      type: String,
      default: null,
    },
    // Grouped data headers
    groupedDataHeadings: {
      required: false,
      type: Array as PropType<string[]>,
      default: () => ['allData'],
    },
    // The group heading name
    groupHeading: {
      required: false,
      type: String,
      default: 'allData',
    },
    // Data rows displayed in the table
    rows: {
      required: true,
      type: Object as PropType<DataRow[]>,
    },
    /**All data (used mainly for checkboxes) */
    allRows: {
      required: true,
      type: Object as PropType<DataRow[]>,
    },
    rowsLength: {
      required: true,
      type: Number,
    },
    // If a checkbox should be shown
    checkbox: {
      required: false,
      type: Object as PropType<FilteringDisplayOptions['checks']>,
      default: undefined,
    },
    // If routing should be used
    route: {
      required: false,
      type: Object as PropType<FilteringDisplayOptions['route']>,
      default: undefined,
    },
    // If a modal should be used
    cellOptions: {
      required: false,
      type: Object as PropType<FilteringDisplayOptions['cellOptions']>,
      default: undefined,
    },
    tableRef: {
      required: false,
      default: null,
      type: Object,
    },
    checkHeader: {
      required: false,
      default: false,
      type: Boolean,
    },
    useListIndex: {
      required: false,
      default: false,
      type: Boolean,
    },
  },
  setup(props, { emit, expose }) {
    const { locale, t } = useI18n({ useScope: 'global' })
    const localeString = computed(() => locale.value as LanguageChoice)

    const isCollapsed: Ref<boolean> = ref(props.collapsed)
    watch(isCollapsed, () => {
      emit('collapsed', { group: props.groupHeading, collapsed: isCollapsed.value })
    })
    const toggleCollapsed = () => {
      isCollapsed.value = !isCollapsed.value
    }
    const returnCellType = (key: string) => {
      return props.columns[key].type
    }
    const sortArrow = computed(() => {
      if (props.sortedValue === null) {
        return ''
      } else if (props.sortedValue) {
        return 'las la-sort-down'
      } else {
        return 'las la-sort-up'
      }
    })
    const toggleSort = () => {
      emit('toggleSort')
      unCheckAll()
    }
    const setSort = (key: string) => {
      emit('setSort', key)
    }
    const columnLength = computed(() => {
      if (props.checkbox?.checklist) return size(props.columns) + 1
      return size(props.columns)
    })

    //checkbox logic
    const {
      allChecked,
      checkedItems,
      mapRowsToCheckedItems,
      showCheckbox,
      toggleAllCheckboxes,
      unCheckAll,
    } = useCheckboxes(props.allRows, props)

    expose({ unCheckAll })
    onBeforeMount(() => {
      unCheckAll()
      mapRowsToCheckedItems()
    })

    const onCheckedChange = () => {
      const retData = []
      for (let index = 0; index < props.allRows.length; index++) {
        if (checkedItems.value[index] !== false) {
          retData.push({ checkedValue: checkedItems.value[index], ...props.allRows[index] })
        }
      }

      emit('checkedChange', retData)
    }
    const rowsHasThisHeader = computed(() => {
      for (const row of props.rows) {
        //@ts-expect-error need to update types for basetable
        if (row.data._group == props.groupHeading) return true
      }
      return false
    })
    watch(
      checkedItems,
      () => {
        onCheckedChange()
      },
      { deep: true }
    )

    return {
      isCollapsed,
      returnCellType,
      toggleCollapsed,
      sortArrow,
      toggleSort,
      setSort,
      columnLength,
      checkedItems,
      allChecked,
      toggleAllCheckboxes,
      showCheckbox,
      localeString,
      rowsHasThisHeader,
      t,
    }
  },
})
</script>
<style scoped>
.q-table {
  position: relative;
}
:deep(.sticky-table-header) {
  position: sticky;
  left: 0;
  top: 0px;
  /* height: 5em; */
  z-index: 3;
  border-bottom: none;
}
:deep(.sticky-table-columns) {
  position: sticky;
  /* top: 0px; */
  top: calc(2.5em + 10px);
  z-index: 1;
}
:deep(.table-header) {
  position: relative;
  z-index: 4;
}
:deep(.row-number--header) {
  width: 10px;
}
:deep(.row-number) {
  background-color: #efefef;
  z-index: 2;
}
:deep(.sticky--left) {
  position: sticky;
  left: 0;
  z-index: 2;
}
:deep(.group-heading) {
  z-index: 5;
}
</style>
